<template>
  <div class="baseball-bate-instance text-center">
    <div ref="VirtualContainer" class="baseball-bate-instance-container-3d"></div>
    <button v-on:click="swingAnimation()" type="button" class="general-cta secondary">
      {{ $t("portfolio.baseballBate.ctaLabel") }}
    </button>
  </div>
</template>

<script>
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import Bat from "@/models/Bat";
import ResourceTracker from "@/models/ResourceTracker";

export default {
  name: "BaseballBateInstance",
  data() {
    return {
      resourceTracker: new ResourceTracker(),
      camera: null,
      scene: null,
      renderer: null,
      frameId: null,
      animation: {
        mixer: null,
        clock: null,
        action: null
      }
    };
  },
  methods: {
    /*
     * @function - initCanvas
     * This function initialize scene, camera, renderer, orbit controls and animation for baseball bat instance
     * @param - none
     */
    initCanvas() {
      const { offsetWidth, offsetHeight } = this.$refs.VirtualContainer;
      const bat = this.resourceTracker.track(new Bat(0, -20, 0, 0, 0, 0));

      // Scene
      this.scene = this.resourceTracker.track(new THREE.Scene());
      this.scene.add(bat);

      // Camera
      this.camera = this.resourceTracker.track(
        new THREE.PerspectiveCamera(75, offsetWidth / offsetHeight, 0.1, 1000)
      );
      this.camera.position.z = 80;

      // Renderer
      this.renderer = this.resourceTracker.track(new THREE.WebGLRenderer({ alpha: true }));
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(offsetWidth, offsetHeight);
      this.$refs.VirtualContainer.appendChild(this.renderer.domElement);

      // setup orbit controls
      const control = this.resourceTracker.track(
        new OrbitControls(this.camera, this.renderer.domElement)
      );
      control.maxPolarAngle = Math.PI;
      control.minDistance = 100;
      control.maxDistance = 2000;

      // setup and call the animation
      this.animation.clock = this.resourceTracker.track(new THREE.Clock());
      this.animation.mixer = this.resourceTracker.track(new THREE.AnimationMixer(bat));
      this.animation.mixer.addEventListener("finished", () => this.animation.action.stop());
      this.swingAnimation();
    },
    /*
     * @function - animateEntry
     * Sets scene animation loop
     * @param - none
     */
    animateEntry() {
      this.frameId = requestAnimationFrame(this.animateEntry);
      if (this.animation.mixer) {
        this.animation.mixer.update(this.animation.clock.getDelta());
      }
      if (this.renderer) {
        this.renderer.render(this.scene, this.camera);
      }
    },
    /*
     * @function - swingAnimation
     * Sets baseball bat swing animation
     * @param - none
     */
    swingAnimation() {
      const period = 2;
      // Position and Rotation animation keyframes
      const rotationZpullback = this.resourceTracker.track(
        new THREE.NumberKeyframeTrack(".rotation[z]", [0.4, 0.6], [0, Math.PI * 0.9])
      );
      const rotationYswing = this.resourceTracker.track(
        new THREE.NumberKeyframeTrack(".rotation[y]", [0.8, 1.2], [0, Math.PI * 2])
      );
      const positionSwing = this.resourceTracker.track(
        new THREE.VectorKeyframeTrack(".position", [0.8, 1.2], [0, -20, 0, 10, -60, -40])
      );
      const positionReturn = this.resourceTracker.track(
        new THREE.VectorKeyframeTrack(".position", [1.4, 1.8], [0, -20, 0, -10, 20, 40])
      );
      const rotationZreturn = this.resourceTracker.track(
        new THREE.NumberKeyframeTrack(".rotation[z]", [1.4, 1.8], [0, -Math.PI * 0.9])
      );
      const clip = this.resourceTracker.track(
        new THREE.AnimationClip("presentation", period, [
          rotationZpullback,
          rotationYswing,
          positionSwing,
          positionReturn,
          rotationZreturn
        ])
      );
      this.animation.action = this.animation.mixer.clipAction(clip);
      this.animation.action.setLoop(THREE.LoopOnce);
      this.animation.action.clampWhenFinished = true;
      this.animation.action.play();
    },
    /*
     * @function - doResize
     * Redefine 3d scene size when windiw is resized
     * @param - none
     */
    doResize() {
      const { offsetWidth, offsetHeight } = this.$refs.VirtualContainer;

      this.camera.aspect = offsetWidth / offsetHeight;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(offsetWidth, offsetHeight);
    },
    /*
     * @function - removeCanvas
     * Clears all 3D scene memory dependencies, remove scene dom element and cancel animation loop
     * @param - none
     */
    removeCanvas() {
      this.resourceTracker.dispose();
      this.$refs.VirtualContainer.removeChild(this.renderer.domElement);
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  },
  mounted() {
    this.initCanvas();
    window.addEventListener("resize", this.doResize);
  },
  beforeDestroy() {
    this.removeCanvas();
    window.removeEventListener("resize", this.doResize);
  }
};
</script>

<style lang="scss" scoped>
.baseball-bate-instance {
  &-container-3d {
    height: 30rem;

    canvas {
      height: 100%;
      width: 100%;
    }
  }
}
</style>
